import { forwardRef, ReactNode } from 'react'

import { Polymorphic } from '../../../utils/PolymorphicUtils'
import { Stack } from '../stack/Stack'

type ButtonProps = {
  size: 'sm' | 'lg'
  variant: 'primary' | 'outline'
  iconStart?: ReactNode
  iconEnd?: ReactNode
}

const sizeClasses: Record<ButtonProps['size'], string> = {
  sm: 'px-4 py-2 text-xs',
  lg: 'px-6 py-3 text-sm',
}

const variantClasses: Record<ButtonProps['variant'], string> = {
  primary:
    'bg-ink-900 text-white hover:-translate-y-0.5 hover:bg-ink-700 dark:bg-white dark:text-ink-900 dark:hover:bg-ink-100',
  outline:
    'border border-ink-300/80 text-ink-700 hover:-translate-y-0.5 hover:border-ink-400 hover:text-ink-900 dark:border-ink-600 dark:text-ink-200 dark:hover:text-white',
}

export const Button = forwardRef(function Button(
  { as, size, variant, iconStart, iconEnd, children, className, ...props },
  ref,
) {
  const classNames = [
    'rounded-full font-semibold transition cursor-pointer',
    sizeClasses[size],
    variantClasses[variant],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Stack
      as={as ?? 'button'}
      className={classNames}
      align="center"
      gap={8}
      {...props}
      ref={ref}
    >
      {iconStart}
      {children}
      {iconEnd}
    </Stack>
  )
}) as Polymorphic<'button', ButtonProps>
